import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Check, ExclamationCircleFill, ArrowRight, ChevronDown } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { formatCurrency } from '../../lib/utils';
import useIsMobile, { useMaxWidth } from '../../hooks/useIsMobile';
import StreakPill from './StreakPill';
import RuleViolationsModal from './RuleViolationsModal';
import { countsTowardViolationStats, isPlanDeviationRuleType, isProfitTargetGaveBack, getProfitTargetViolationLabel } from '../../utils/violationLimitActual';
import { getRuleDisplayName } from '../../constants/ruleTypeDefinitions';
import { DASHBOARD_ROUTES } from '../../constants/dashboardRoutes';
import { DASHBOARD_CARD_RADIUS, getDashboardCardStyle } from '../../constants/dashboardLayout';

function fmtPnl(val) {
  if (val === null || val === undefined) return '—';
  const sign = val >= 0 ? '+' : '';
  return `${sign}${formatCurrency(val)}`;
}

function fmtPct(val, decimals = 1) {
  if (val === null || val === undefined) return '';
  const sign = val >= 0 ? '+' : '';
  return `${sign}${val.toFixed(decimals)}%`;
}

function InfoTip({ text, color }) {
  return (
    <OverlayTrigger placement="top" overlay={<Tooltip>{text}</Tooltip>}>
      <span style={{ cursor: 'help', color, opacity: 0.95, fontSize: '0.75rem', lineHeight: 1 }}>ⓘ</span>
    </OverlayTrigger>
  );
}

/**
 * ConstraintBand – Health "today" answer + snapshot checklist.
 *
 * Answers in ~3 seconds: Did I break rules today? Which ones? What's next?
 * Snapshot checklist shows limit proximity; rules panel lists hard breaks plus soft
 * Target Daily Profit “hit then fell below” notes (not counted as broken rules).
 */
const ConstraintBand = ({
  todayMetrics,
  todayChange: todayChangeProp = 0,
  todayJournalCompliance = {},
  todayPerformanceSummary = {},
  realtimeMetrics = {},
  accountId,
  todayViolations: todayViolationsProp = [],
  onJournalMissingClick,
  onSettingsClick,
  onTradesClick,
  isTradingLocked: isTradingLockedProp = false,
  lockoutEndsText: lockoutEndsTextProp = null,
  /** Optional `?today=` / `?v=` demo overrides for the Today strip */
  demoOverrides = null,
}) => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isNarrowBand = useMaxWidth(1100);
  const stackSections = isMobile || isNarrowBand;
  const [showViolationsModal, setShowViolationsModal] = useState(false);
  const [planDeviationsOpen, setPlanDeviationsOpen] = useState(false);

  const demo = demoOverrides || null;

  const todayData = todayMetrics?.metrics || todayMetrics || {};
  const {
    trades_taken: tradesTakenRaw = 0,
    max_trades: maxTradesRaw = 8,
    daily_loss_limit: apiDailyLossLimit = 0,
    daily_loss_breached: dailyLossBreachedRaw = false,
  } = todayData;

  const {
    journals_created: journalsCreatedRaw = 0,
    closed_positions: closedPositions = 0,
    new_positions_opened: positionsOpenedLegacy = 0,
  } = todayJournalCompliance;
  const positionsRequiringJournalRaw = closedPositions > 0 ? closedPositions : positionsOpenedLegacy;

  const todayChange = demo?.todayChange ?? todayChangeProp;
  const tradesTaken = demo?.tradesTaken ?? tradesTakenRaw;
  const maxTrades = demo?.maxTrades ?? maxTradesRaw;
  const journalsCreated = demo?.journalsCreated ?? journalsCreatedRaw;
  const positionsRequiringJournal = demo?.positionsRequiringJournal ?? positionsRequiringJournalRaw;
  const isTradingLocked = demo?.isTradingLocked ?? isTradingLockedProp;
  const lockoutEndsText = demo?.lockoutEndsText ?? lockoutEndsTextProp;
  const todayViolations = demo?.todayViolations ?? todayViolationsProp;
  const todayChangePercent = demo?.todayChangePercent ?? realtimeMetrics?.todayChangePercent;

  const todayWinRate = demo?.todayWinRate != null
    ? demo.todayWinRate
    : Number(todayPerformanceSummary?.winRate);
  const todayWinRatePct = Number.isFinite(todayWinRate)
    ? Math.max(0, Math.min(100, todayWinRate * 100))
    : 0;
  const todayWinningTrades = demo?.todayWinningTrades
    ?? (Number(todayPerformanceSummary?.winningTrades) || 0);
  const todayTotalTrades = demo?.todayTotalTrades
    ?? (Number(todayPerformanceSummary?.totalTrades) || 0);

  const dailyLossLimitRaw = apiDailyLossLimit < 0
    ? apiDailyLossLimit
    : apiDailyLossLimit > 0
      ? -Math.abs(apiDailyLossLimit)
      : 0;
  const dailyLossLimit = demo?.dailyLossLimit ?? dailyLossLimitRaw;
  const dailyLossBreached = demo?.dailyLossBreached ?? dailyLossBreachedRaw;
  const isBreached = dailyLossBreached
    || (dailyLossLimit !== 0 && todayChange < 0 && Math.abs(todayChange) >= Math.abs(dailyLossLimit));
  const journalBehind = positionsRequiringJournal > 0 && journalsCreated < positionsRequiringJournal;
  const journalMissing = Math.max(0, positionsRequiringJournal - journalsCreated);
  const tradesExceeded = tradesTaken > maxTrades && maxTrades > 0;
  const tradesAtCap = tradesTaken === maxTrades && maxTrades > 0;

  const todayViolationsByType = useMemo(() => {
    const hard = {};
    const plan = {};
    (todayViolations || []).forEach((v) => {
      if (!v.rule_type) return;
      if (isPlanDeviationRuleType(v.rule_type)) {
        plan[v.rule_type] = (plan[v.rule_type] || 0) + 1;
        return;
      }
      if (!countsTowardViolationStats(v)) return;
      hard[v.rule_type] = (hard[v.rule_type] || 0) + 1;
    });
    return { hard, plan };
  }, [todayViolations]);

  /** Soft advisory: hit Target Daily Profit earlier, now under target (not a hard rule break). */
  const profitTargetGaveBack = useMemo(
    () => (todayViolations || []).find((v) => isProfitTargetGaveBack(v)) || null,
    [todayViolations]
  );

  const todayViolationCount = useMemo(
    () => Object.values(todayViolationsByType.hard).reduce((sum, n) => sum + (typeof n === 'number' ? n : 0), 0),
    [todayViolationsByType]
  );

  const planDeviationCount = useMemo(
    () => Object.values(todayViolationsByType.plan).reduce((sum, n) => sum + (typeof n === 'number' ? n : 0), 0),
    [todayViolationsByType]
  );

  const brokenRuleLabels = useMemo(
    () => Object.entries(todayViolationsByType.hard)
      .sort((a, b) => b[1] - a[1])
      .map(([type, count]) => ({
        type,
        count,
        label: getRuleDisplayName(type) || type,
      })),
    [todayViolationsByType]
  );

  const planDeviationLabels = useMemo(
    () => Object.entries(todayViolationsByType.plan)
      .sort((a, b) => b[1] - a[1])
      .map(([type, count]) => ({
        type,
        count,
        label: getRuleDisplayName(type) || type,
      })),
    [todayViolationsByType]
  );

  const issueCount = [
    todayViolationCount > 0,
    isBreached,
    tradesExceeded,
    journalBehind,
    isTradingLocked,
    !!profitTargetGaveBack,
  ].filter(Boolean).length;

  const statusTone = isTradingLocked || isBreached || todayViolationCount > 0 || tradesExceeded
    ? 'danger'
    : journalBehind || profitTargetGaveBack
      ? 'warning'
      : 'ok';

  const statusHeadline = (() => {
    if (isTradingLocked) return 'Trading locked';
    if (todayViolationCount > 0) {
      return todayViolationCount === 1
        ? '1 rule broken today'
        : `${todayViolationCount} rules broken today`;
    }
    if (isBreached) return 'Daily loss limit hit';
    if (tradesExceeded) return 'Trade count exceeded';
    if (profitTargetGaveBack) return 'Profit target slipped';
    if (journalBehind) return 'Journal catching up';
    return 'On track today';
  })();

  const statusSubline = (() => {
    if (brokenRuleLabels.length > 0) {
      return (
        <span
          style={{
            display: 'inline-flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '6px 10px',
          }}
        >
          {brokenRuleLabels.slice(0, 3).map((r, idx) => (
            <React.Fragment key={r.type}>
              {idx > 0 && (
                <span aria-hidden style={{ color: colors.textMuted }}>
                  ·
                </span>
              )}
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span>{r.label}</span>
                <span
                  aria-label={`${r.count} violation${r.count === 1 ? '' : 's'}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 20,
                    height: 20,
                    padding: '0 5px',
                    borderRadius: 999,
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    lineHeight: 1,
                    color: colors.danger,
                    background: `${colors.danger}18`,
                    border: `1px solid ${colors.danger}44`,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {r.count}
                </span>
              </span>
            </React.Fragment>
          ))}
        </span>
      );
    }
    if (profitTargetGaveBack) {
      return 'Hit Target Daily Profit earlier, now below target';
    }
    if (isTradingLocked && lockoutEndsText) return `Lockout ends ${lockoutEndsText}`;
    if (journalBehind) return `${journalMissing} trade${journalMissing === 1 ? '' : 's'} still need a journal`;
    if (tradesAtCap) return 'At max trades for today';
    return 'No rule violations so far today';
  })();

  const nextActions = useMemo(() => {
    const actions = [];
    if (isTradingLocked) {
      actions.push({
        key: 'lockout',
        label: lockoutEndsText ? `Trading allowed: ${lockoutEndsText}` : 'Wait for lockout to end',
        tone: 'danger',
      });
    }
    if (journalBehind && typeof onJournalMissingClick === 'function') {
      actions.push({
        key: 'journal',
        label: `Journal ${journalMissing} missing trade${journalMissing === 1 ? '' : 's'}`,
        onClick: onJournalMissingClick,
        tone: 'warning',
      });
    }
    if (todayViolationCount > 0 || isBreached || tradesExceeded) {
      actions.push({
        key: 'rules',
        label: 'Review trading rules',
        onClick: onSettingsClick || (() => navigate(DASHBOARD_ROUTES.tradingRules)),
        tone: 'danger',
      });
    } else if (profitTargetGaveBack) {
      actions.push({
        key: 'profit-gave',
        label: 'Review Target Daily Profit',
        onClick: () => setShowViolationsModal(true),
        tone: 'warning',
      });
    }
    if (journalBehind) {
      actions.push({
        key: 'journals-page',
        label: 'Open journals',
        onClick: () => navigate(DASHBOARD_ROUTES.journals),
        tone: 'neutral',
      });
    }
    if (actions.length === 0) {
      actions.push({
        key: 'keep',
        label: 'Keep following your plan',
        tone: 'ok',
      });
    }
    return actions.slice(0, 3);
  }, [
    isTradingLocked,
    lockoutEndsText,
    journalBehind,
    journalMissing,
    onJournalMissingClick,
    todayViolationCount,
    isBreached,
    tradesExceeded,
    profitTargetGaveBack,
    onSettingsClick,
    navigate,
  ]);

  const activityRows = [
    {
      label: 'P&L Today',
      ok: !isBreached,
      value: fmtPnl(todayChange),
      subText: todayChangePercent != null ? fmtPct(todayChangePercent) : null,
      alert: isBreached ? 'limit hit' : null,
    },
    ...(dailyLossLimit !== 0 ? [{
      label: 'Risk Limit',
      ok: !isBreached,
      value: fmtPnl(Math.min(0, todayChange)),
      limitValue: formatCurrency(dailyLossLimit),
      alert: isBreached ? 'breached' : null,
    }] : []),
    {
      label: 'Trades',
      ok: !tradesExceeded && !tradesAtCap,
      value: `${tradesTaken} / ${maxTrades}`,
      alert: tradesExceeded ? 'exceeded' : tradesAtCap ? 'at max' : null,
      tooltip: 'Counts each position opened today (ET), including still-open ones — same as Max Trades Per Day.',
      clickable: typeof onTradesClick === 'function',
      onClick: onTradesClick,
    },
    {
      label: 'Journal',
      ok: !journalBehind,
      value: `${journalsCreated} / ${positionsRequiringJournal}`,
      alert: journalBehind ? `${journalMissing} missing` : null,
      tooltip: 'Closed trades today that still need a journal entry.',
    },
    {
      label: 'Win Rate',
      ok: true,
      neutral: true,
      value: `${todayWinRatePct.toFixed(0)}%`,
      subText: todayTotalTrades > 0 ? `${todayWinningTrades} / ${todayTotalTrades} wins` : 'no closed trades',
      tooltip: 'Based on positions closed today, even if opened earlier.',
    },
  ];

  const getProgressPercent = (row) => {
    if (row.label === 'Risk Limit') {
      if (!dailyLossLimit) return 0;
      const lossUsed = Math.abs(Math.min(0, todayChange));
      return Math.max(0, Math.min(100, (lossUsed / Math.abs(dailyLossLimit)) * 100));
    }
    if (row.label === 'Trades') {
      return maxTrades > 0 ? Math.max(0, Math.min(100, (tradesTaken / maxTrades) * 100)) : 0;
    }
    if (row.label === 'Journal') {
      if (positionsRequiringJournal <= 0) return 100;
      return Math.max(0, Math.min(100, (journalsCreated / positionsRequiringJournal) * 100));
    }
    if (row.label === 'Win Rate') return todayWinRatePct;
    if (row.label === 'P&L Today' && dailyLossLimit) {
      const lossUsed = Math.abs(Math.min(0, todayChange));
      return Math.max(0, Math.min(100, (lossUsed / Math.abs(dailyLossLimit)) * 100));
    }
    return todayChange > 0 ? Math.min(100, 40 + Math.abs(realtimeMetrics?.todayChangePercent || 0) * 4) : 0;
  };

  const getProgressColor = (row, pct) => {
    if (row.label === 'Win Rate') return colors.primary;
    if (row.label === 'Risk Limit' || row.label === 'Trades') {
      return pct >= 100 ? colors.danger : colors.success;
    }
    if (row.label === 'Journal') {
      if (positionsRequiringJournal <= 0) return colors.success;
      if (pct >= 100) return colors.success;
      return colors.warning || '#F59E0B';
    }
    if (row.label === 'P&L Today') {
      if (isBreached) return colors.danger;
      return todayChange >= 0 ? colors.success : (colors.warning || '#F59E0B');
    }
    if (pct >= 100) return colors.danger;
    if (pct >= 80) return colors.warning || '#F59E0B';
    return colors.textMuted;
  };

  const toneColor = statusTone === 'ok'
    ? colors.success
    : statusTone === 'warning'
      ? (colors.warning || '#F59E0B')
      : colors.danger;

  const sectionPadStyle = { padding: stackSections ? '16px' : '20px 22px' };
  const linkBtnStyle = {
    background: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    fontSize: '0.78rem',
    fontWeight: 600,
    color: colors.text,
    textDecoration: 'underline',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  };

  return (
    <Card style={{ ...getDashboardCardStyle(colors, { radius: DASHBOARD_CARD_RADIUS }), marginBottom: 24 }}>
      <Card.Body style={{ padding: 0 }}>
        {/* ── 3-second answer ───────────────────────────────── */}
        <div
          data-testid="health-today-status"
          style={{
            padding: stackSections ? '16px' : '18px 22px',
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            flexDirection: stackSections ? 'column' : 'row',
            alignItems: stackSections ? 'stretch' : 'center',
            justifyContent: 'space-between',
            gap: stackSections ? 14 : 20,
            background: statusTone === 'ok'
              ? `linear-gradient(90deg, ${colors.success}12, transparent 55%)`
              : statusTone === 'warning'
                ? `linear-gradient(90deg, ${(colors.warning || '#F59E0B')}14, transparent 55%)`
                : `linear-gradient(90deg, ${colors.danger}14, transparent 55%)`,
          }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontSize: '0.68rem',
                fontWeight: 700,
                letterSpacing: '0.07em',
                textTransform: 'uppercase',
                color: colors.textMuted,
                marginBottom: 6,
              }}
            >
              Today
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {statusTone === 'ok'
                ? <Check size={22} style={{ color: toneColor, flexShrink: 0 }} />
                : <ExclamationCircleFill size={20} color={toneColor} style={{ flexShrink: 0 }} />}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: isMobile ? '1.15rem' : '1.35rem', fontWeight: 700, color: colors.text, lineHeight: 1.2 }}>
                  {statusHeadline}
                </div>
                <div style={{ fontSize: '0.84rem', color: colors.textLight, marginTop: 4, lineHeight: 1.35 }}>
                  {statusSubline}
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              alignItems: stackSections ? 'stretch' : 'flex-end',
              flexShrink: 0,
              minWidth: stackSections ? 0 : 200,
            }}
          >
            <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: colors.textMuted }}>
              What&apos;s next
            </div>
            {nextActions.map((action) => (
              action.onClick ? (
                <button
                  key={action.key}
                  type="button"
                  onClick={action.onClick}
                  style={{
                    ...linkBtnStyle,
                    color: action.tone === 'ok' ? colors.success : colors.text,
                    justifyContent: stackSections ? 'flex-start' : 'flex-end',
                  }}
                >
                  {action.label}
                  <ArrowRight size={12} />
                </button>
              ) : (
                <span key={action.key} style={{ fontSize: '0.84rem', color: colors.textLight, fontWeight: 500 }}>
                  {action.label}
                </span>
              )
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: stackSections ? '1fr' : 'minmax(0, 1.15fr) minmax(0, 0.85fr)' }}>
          {/* ── Snapshot checklist ─────────────────────────── */}
          <div
            data-testid="health-today-snapshot"
            style={{
              ...sectionPadStyle,
              borderRight: stackSections ? 'none' : `1px solid ${colors.border}`,
              borderBottom: stackSections ? `1px solid ${colors.border}` : 'none',
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 8 }}>
              <div
                style={{
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: colors.text,
                }}
              >
                Today&apos;s Snapshot
              </div>
              {onSettingsClick && (
                <button
                  type="button"
                  onClick={onSettingsClick}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${colors.primary}`,
                    color: colors.primary,
                    borderRadius: 999,
                    padding: '2px 10px',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.6,
                  }}
                >
                  Trading Rules
                </button>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
              {activityRows.map((row) => {
                const { label, ok, neutral, value, subText, alert, limitValue, tooltip, clickable, onClick } = row;
                const pct = getProgressPercent(row);
                const progressColor = getProgressColor(row, pct);
                const isSevere = label === 'Risk Limit' && !ok;
                const issueColor = isSevere ? colors.danger : (colors.warning || '#F59E0B');
                const openTrades = clickable && typeof onClick === 'function' ? onClick : null;
                return (
                  <div
                    key={label}
                    data-testid={`health-snapshot-row-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}
                  >
                    <div style={{ width: 22, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                      {neutral
                        ? <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: colors.primary }} />
                        : ok
                          ? <Check size={19} style={{ color: colors.success }} />
                          : <ExclamationCircleFill size={18} color={issueColor} />}
                    </div>
                    <div style={{ minWidth: 0, flex: '1 1 auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div style={{ minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 8 }}>
                        {openTrades ? (
                          <button
                            type="button"
                            onClick={openTrades}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              margin: 0,
                              fontSize: '0.8rem',
                              color: ok ? colors.textMuted : colors.textLight,
                              flex: '0 0 auto',
                              whiteSpace: 'nowrap',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              fontWeight: 500,
                            }}
                            title="See which positions count toward Max Trades"
                          >
                            {label}
                          </button>
                        ) : (
                          <span style={{ fontSize: '0.8rem', color: ok ? colors.textMuted : colors.textLight, flex: '0 0 auto', whiteSpace: 'nowrap' }}>
                            {label}
                          </span>
                        )}
                        {tooltip && <InfoTip text={tooltip} color={colors.textMuted} />}
                        {alert && !ok && (
                          label === 'Journal' && typeof onJournalMissingClick === 'function' ? (
                            <button
                              type="button"
                              onClick={onJournalMissingClick}
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                margin: 0,
                                fontWeight: 500,
                                color: issueColor,
                                fontSize: '0.73rem',
                                whiteSpace: 'nowrap',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                            >
                              {alert}
                            </button>
                          ) : openTrades ? (
                            <button
                              type="button"
                              onClick={openTrades}
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                margin: 0,
                                fontWeight: 500,
                                color: issueColor,
                                fontSize: '0.73rem',
                                whiteSpace: 'nowrap',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                            >
                              {alert}
                            </button>
                          ) : (
                            <span style={{ fontWeight: 500, color: issueColor, fontSize: '0.73rem', whiteSpace: 'nowrap' }}>
                              {alert}
                            </span>
                          )
                        )}
                        {openTrades ? (
                          <button
                            type="button"
                            onClick={openTrades}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              margin: 0,
                              marginLeft: 'auto',
                              fontSize: isMobile ? '0.82rem' : '0.9rem',
                              fontWeight: 600,
                              color: colors.text,
                              minWidth: 0,
                              textAlign: 'right',
                              lineHeight: 1.35,
                              whiteSpace: 'nowrap',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                            }}
                            title="See which positions count toward Max Trades"
                          >
                            {value}
                          </button>
                        ) : (
                        <span
                          style={{
                            fontSize: isMobile ? '0.82rem' : '0.9rem',
                            fontWeight: 600,
                            color:
                              label === 'P&L Today'
                                ? (todayChange > 0 ? colors.success : todayChange < 0 ? colors.danger : colors.textMuted)
                                : colors.text,
                            minWidth: 0,
                            marginLeft: 'auto',
                            textAlign: 'right',
                            lineHeight: 1.35,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {limitValue ? (
                            <>
                              <span style={{ fontWeight: 400, color: todayChange < 0 ? issueColor : colors.textMuted }}>
                                {value}
                              </span>
                              <span style={{ color: colors.textMuted, fontWeight: 400 }}> / </span>
                              <span style={{ fontWeight: 700, color: !ok ? issueColor : colors.text }}>{limitValue}</span>
                            </>
                          ) : (
                            <>
                              {subText && (
                                <span style={{ fontWeight: 400, color: colors.textMuted, marginRight: 4, fontSize: '0.78rem' }}>
                                  {subText}
                                </span>
                              )}
                              {value}
                            </>
                          )}
                        </span>
                        )}
                      </div>
                      <div
                        role={openTrades ? 'button' : undefined}
                        tabIndex={openTrades ? 0 : undefined}
                        onClick={openTrades || undefined}
                        onKeyDown={openTrades ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openTrades();
                          }
                        } : undefined}
                        style={{
                          position: 'relative',
                          height: 6,
                          backgroundColor: colors.borderLight,
                          borderRadius: 6,
                          overflow: 'hidden',
                          cursor: openTrades ? 'pointer' : undefined,
                        }}
                      >
                        <div
                          style={{
                            width: '100%',
                            height: '100%',
                            background: progressColor,
                            borderRadius: 6,
                            transformOrigin: 'left center',
                            transform: `scaleX(${Math.max(0, Math.min(1, pct / 100))})`,
                            transition: 'transform 0.25s ease',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Which rules / detail ───────────────────────── */}
          <div data-testid="health-rules-broken-today" style={{ ...sectionPadStyle, display: 'flex', flexDirection: 'column', minWidth: 0, gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
              <div
                style={{
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: colors.text,
                }}
              >
                Rules broken today
              </div>
              <span style={{ fontSize: '0.78rem', color: colors.textMuted }}>
                {todayViolationCount} broken
                {profitTargetGaveBack ? ' · 1 note' : ''}
                {issueCount > 1 ? ` · ${issueCount} issues` : ''}
              </span>
            </div>

            {brokenRuleLabels.length > 0 && (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {brokenRuleLabels.map((row) => (
                  <li
                    key={row.type}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 12,
                      padding: '8px 10px',
                      borderRadius: 8,
                      background: `${colors.danger}12`,
                      border: `1px solid ${colors.danger}33`,
                    }}
                  >
                    <span style={{ fontSize: '0.84rem', fontWeight: 600, color: colors.text }}>{row.label}</span>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 22,
                        height: 22,
                        padding: '0 6px',
                        borderRadius: 999,
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        color: colors.danger,
                        background: `${colors.danger}18`,
                        border: `1px solid ${colors.danger}44`,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {row.count}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {profitTargetGaveBack && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  padding: '8px 10px',
                  borderRadius: 8,
                  background: `${colors.warning || '#f59e0b'}14`,
                  border: `1px solid ${colors.warning || '#f59e0b'}44`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <span style={{ fontSize: '0.84rem', fontWeight: 600, color: colors.text }}>
                    {getProfitTargetViolationLabel(profitTargetGaveBack)}
                  </span>
                  <span
                    style={{
                      flexShrink: 0,
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: colors.warning || '#f59e0b',
                      padding: '2px 6px',
                      borderRadius: 4,
                      background: `${colors.warning || '#f59e0b'}18`,
                      border: `1px solid ${colors.warning || '#f59e0b'}44`,
                    }}
                  >
                    Note
                  </span>
                </div>
                <span style={{ fontSize: '0.75rem', color: colors.textMuted, lineHeight: 1.35 }}>
                  Not a broken rule — you hit take-profit earlier, then finished under the target.
                </span>
              </div>
            )}

            {brokenRuleLabels.length === 0 && !profitTargetGaveBack && (
              <div style={{ fontSize: '0.84rem', color: colors.textMuted }}>
                None so far today
              </div>
            )}

            {planDeviationCount > 0 && (
              <div
                style={{
                  marginTop: 4,
                  borderRadius: 8,
                  background: colors.bgSecondary || `${colors.textMuted}14`,
                  border: `1px solid ${colors.border}`,
                  overflow: 'hidden',
                }}
              >
                <button
                  type="button"
                  onClick={() => setPlanDeviationsOpen((v) => !v)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                    padding: '8px 10px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: colors.textMuted,
                  }}
                >
                  <span style={{ fontSize: '0.78rem', fontWeight: 600 }}>
                    Plan deviations
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 18,
                        height: 18,
                        marginLeft: 8,
                        padding: '0 5px',
                        borderRadius: 999,
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                        color: colors.textLight,
                        background: `${colors.textMuted}28`,
                        border: `1px solid ${colors.border}`,
                        verticalAlign: 'middle',
                      }}
                    >
                      {planDeviationCount}
                    </span>
                  </span>
                  <ChevronDown
                    size={14}
                    style={{
                      transform: planDeviationsOpen ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.15s ease',
                      flexShrink: 0,
                    }}
                  />
                </button>
                {planDeviationsOpen && (
                  <div style={{ padding: '0 10px 10px' }}>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {planDeviationLabels.map((row) => (
                        <li
                          key={row.type}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 10,
                            fontSize: '0.8rem',
                            color: colors.textLight,
                          }}
                        >
                          <span>{row.label}</span>
                          <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{row.count}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div
              style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'center' : 'center',
                justifyContent: isMobile ? 'center' : 'space-between',
                gap: isMobile ? 10 : 12,
                marginTop: 'auto',
                flexWrap: 'wrap',
              }}
            >
              <StreakPill />
              <button
                type="button"
                onClick={() => setShowViolationsModal(true)}
                style={{
                  ...linkBtnStyle,
                  color: colors.textMuted,
                  fontWeight: 500,
                  fontStyle: 'italic',
                  justifyContent: isMobile ? 'center' : undefined,
                }}
              >
                All recent violations
              </button>
            </div>
          </div>
        </div>
      </Card.Body>

      <RuleViolationsModal
        show={showViolationsModal}
        onHide={() => setShowViolationsModal(false)}
        accountId={accountId}
      />
    </Card>
  );
};

export default ConstraintBand;
